import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import FadeIn from '../components/common/FadeIn.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import {
  ArrowLongRightIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  UserIcon,
} from '../components/icons/Icons.jsx';
import { fetchPublishedWhatsNew, partitionWhatsNew } from '../lib/whatsNew.js';
import WhatsNewCard from '../components/whatsNew/WhatsNewCard.jsx';
import WhatsNewDetailModal from '../components/whatsNew/WhatsNewDetailModal.jsx';
import { fetchLatestPublishedIssue, formatIssueMonth, issueContent, issuePath } from '../lib/newsletter.js';

const HOMEPAGE_WHATSNEW_LIMIT = 4;

export default function Home() {
  const { t, i18n } = useTranslation();
  const { isSignedIn } = useAuth();
  const [latestIssue, setLatestIssue] = useState(null);
  const [whatsNewItems, setWhatsNewItems] = useState(null);
  const [whatsNewError, setWhatsNewError] = useState(null);
  const [whatsNewDetail, setWhatsNewDetail] = useState(null);

  // Lightweight fetch — same query the /whats-new page uses, but we only
  // surface the next few upcoming entries (or the most-recent past ones if
  // there's nothing upcoming) and link to /whats-new for the full list.
  useEffect(() => {
    let cancelled = false;
    fetchPublishedWhatsNew()
      .then((rows) => !cancelled && setWhatsNewItems(rows))
      .catch((err) => !cancelled && setWhatsNewError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  // Latest newsletter issue for the teaser band. Failure is silent for the
  // same reason as the What's New band below.
  useEffect(() => {
    let cancelled = false;
    fetchLatestPublishedIssue()
      .then((row) => !cancelled && setLatestIssue(row))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const latestContent = useMemo(
    () => (latestIssue ? issueContent(latestIssue, i18n.resolvedLanguage) : null),
    [latestIssue, i18n.resolvedLanguage],
  );

  const whatsNewPreview = useMemo(() => {
    if (!whatsNewItems) return [];
    const { upcoming, past } = partitionWhatsNew(whatsNewItems);
    const combined = [...upcoming, ...past];
    return combined.slice(0, HOMEPAGE_WHATSNEW_LIMIT);
  }, [whatsNewItems]);

  return (
    <>
      {/* ── HERO ── */}
      <section className="hero">
        <div className="relative z-10 max-w-[800px]">
          <div className="hero-badge">{t('home.hero.badge')}</div>
          <h1 className="hero-title">
            <Trans
              i18nKey="home.hero.title"
              components={{ em: <em />, br: <br /> }}
            />
          </h1>
          <p className="hero-lede">{t('home.hero.lede')}</p>
          <Link to="/explore" className="btn-primary">
            {t('home.hero.cta')}
            <ArrowLongRightIcon style={{ width: 18, height: 18 }} />
          </Link>
        </div>
        {!isSignedIn && (
          <a href="#signin" className="scroll-hint" aria-label={t('home.scroll')}>
            {t('home.scroll')}
            <ChevronDownIcon style={{ width: 16, height: 16 }} />
          </a>
        )}
      </section>

      {/* ── SIGN-IN CTA (only shown to signed-out visitors) ── */}
      {!isSignedIn && (
        <section id="signin" className="cta-section">
          <FadeIn className="cta-box">
            <div className="section-label">{t('home.signIn.label')}</div>
            <h2 className="cta-title">{t('home.signIn.title')}</h2>
            <p className="cta-body">{t('home.signIn.description')}</p>
            <Link to="/auth" className="btn-primary">
              {t('home.signIn.cta')}
              <UserIcon style={{ width: 18, height: 18 }} />
            </Link>
          </FadeIn>
        </section>
      )}

      {/* ── LATEST NEWSLETTER ── */}
      {latestIssue && (
        <section className="cta-section">
          <FadeIn className="cta-box">
            <div className="section-label">
              {t('home.newsletter.label')} · {formatIssueMonth(latestIssue.year, latestIssue.month, i18n.resolvedLanguage)}
            </div>
            <h2 className="cta-title">{latestContent.title}</h2>
            {latestContent.summary && <p className="cta-body">{latestContent.summary}</p>}
            <Link to={issuePath(latestIssue)} className="btn-primary">
              {t('home.newsletter.cta')}
              <ArrowLongRightIcon style={{ width: 18, height: 18 }} />
            </Link>
            <div className="mt-5">
              <Link to="/newsletter" className="card-link inline-flex items-center gap-1">
                {t('home.newsletter.archive')}
                <ArrowRightIcon />
              </Link>
            </div>
          </FadeIn>
        </section>
      )}

      {/* ── WHAT'S NEW PREVIEW ── */}
      {/* Last band on the homepage: a compact mirror of the /whats-new page
          showing the next few upcoming items (or most-recent past ones if
          nothing is upcoming). The cards are the same component used on the
          full page, so opening one shows the standard detail modal. */}
      {whatsNewPreview.length > 0 && (
        <section className="section">
          <header className="section-header">
            <div className="section-label">{t('home.tiles.whats_new.title')}</div>
            <h2 className="section-title">{t('whatsNew.heading')}</h2>
            <p className="section-subtitle">
              {t('home.tiles.whats_new.description')}
            </p>
          </header>

          {/* With a single card the 2-column grid would leave it pinned to
              the left half of the row, which looks lopsided. Use a flex
              layout with justify-center and per-card width capping so the
              cards always land in a balanced, centred composition — one
              card centred, two side-by-side, three or four wrapping evenly. */}
          <div className="mx-auto flex max-w-shell flex-wrap justify-center gap-3 px-6">
            {whatsNewPreview.map((item) => (
              <div
                key={item.id}
                className="w-full max-w-xl sm:w-[calc(50%-0.375rem)]"
              >
                <WhatsNewCard item={item} onOpen={setWhatsNewDetail} />
              </div>
            ))}
          </div>

          <div className="mx-auto mt-8 max-w-shell px-6 text-center">
            <Link to="/whats-new" className="card-link inline-flex items-center gap-1">
              {t('home.tiles.whats_new.cta')}
              <ArrowRightIcon />
            </Link>
          </div>
        </section>
      )}

      {/* WhatsNewError is intentionally swallowed here — the homepage band
          is a nice-to-have, and the dedicated /whats-new page surfaces any
          real fetch errors. */}

      <WhatsNewDetailModal
        item={whatsNewDetail}
        onClose={() => setWhatsNewDetail(null)}
        onDeleted={(id) =>
          setWhatsNewItems((prev) => prev?.filter((m) => m.id !== id) ?? prev)
        }
      />
    </>
  );
}
